"use client"

import {useEffect} from "react";

/**
 * Keys that move focus or activate a control — i.e. the user is driving with the keyboard.
 * Plain typing must NOT flip the modality: otherwise clicking into a field with the mouse and
 * typing would pop a focus ring around it.
 */
const NAVIGATION_KEYS = new Set([
    "Tab",
    "Enter",
    "Escape",
    " ",
    "ArrowUp",
    "ArrowDown",
    "ArrowLeft",
    "ArrowRight",
    "Home",
    "End",
    "PageUp",
    "PageDown",
]);

/**
 * Stamps the last input modality on <html> so globals.css can show the focus ring to keyboard
 * users only. The browser's own :focus-visible heuristic isn't enough: Chrome propagates it
 * across script-driven focus moves, and Radix focuses popover/dialog content programmatically,
 * so pointer users end up matching :focus-visible. Gating on observed user input instead means
 * a ring appears if and only if a navigation key was actually pressed.
 *
 * The attribute is never rendered server-side. Absent === pointer === no ring, so there is no
 * hydration mismatch and nothing to suppress.
 */
export const InputModalityTracker = () => {
    useEffect(() => {
        const setModality = (modality: "keyboard" | "pointer") => {
            document.documentElement.dataset.inputModality = modality;
        };

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.ctrlKey || event.metaKey || event.altKey) return;
            if (!NAVIGATION_KEYS.has(event.key)) return;
            setModality("keyboard");
        };

        const onPointerDown = () => setModality("pointer");

        // Capture phase — Radix's DismissableLayer stops propagation on some of these events,
        // so a bubble-phase listener would never see them.
        document.addEventListener("keydown", onKeyDown, true);
        document.addEventListener("pointerdown", onPointerDown, true);

        return () => {
            document.removeEventListener("keydown", onKeyDown, true);
            document.removeEventListener("pointerdown", onPointerDown, true);
        };
    }, []);

    return null;
}
